<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ChartBarIcon, ArrowDownTrayIcon, ArrowPathIcon, Cog6ToothIcon, FunnelIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import AvatarMultiselect from '@/components/common/selects/AvatarMultiselect.vue'
import {
  useH3aStatsStore,
  type H3aExportFormat,
  type H3aDateField,
  type H3aBu,
  type H3aMatchKey,
  type H3aMonthlySetting,
  type H3aSortBy,
  type H3aDetailRow,
} from '@/stores/useH3aStatsStore'

const store = useH3aStatsStore()

// ── Hauteur de page : remplir exactement l'espace restant sous l'offset du haut ─
// Le layout global (App.vue) est en `min-h-screen`, il ne borne donc jamais ses
// enfants. On mesure la position du haut de la page (qui varie selon les bandeaux,
// le bandeau d'impersonation, etc.) et on fixe la hauteur à `innerHeight - top`,
// pour que le tableau prenne le reste et scrolle en interne, sans déborder le body.
const pageRoot = ref<HTMLElement | null>(null)
const pageHeight = ref('100dvh')
let pageRO: ResizeObserver | null = null
function updatePageHeight() {
  const el = pageRoot.value
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY
  const next = `${Math.max(240, Math.round(window.innerHeight - top))}px`
  if (next !== pageHeight.value) pageHeight.value = next
}
onMounted(() => {
  nextTick(updatePageHeight)
  window.addEventListener('resize', updatePageHeight)
  pageRO = new ResizeObserver(() => updatePageHeight())
  pageRO.observe(document.body)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePageHeight)
  pageRO?.disconnect()
})

// ── Filtres ──────────────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear()
const yearOptions = Array.from({ length: CURRENT_YEAR - 2021 }, (_, i) => CURRENT_YEAR - i)
const BU_OPTIONS: { value: H3aBu; label: string }[] = [
  { value: 'odyc', label: 'ODY-C' },
  { value: 'unixo', label: 'Unixo' },
  { value: 'unixoNegoce', label: 'Unixo Négoce' },
  { value: 'odycVad', label: 'ODY-C VAD' },
]
const WINDOW_OPTIONS = [3, 6, 12]
const selectedBu = ref<H3aBu>('odyc')
const selectedYear = ref(2025)
const selectedDateField = ref<H3aDateField>('date')
const selectedMatchKey = ref<H3aMatchKey>('siret')
const selectedWindowMonths = ref(12)
const selectedCommercial = ref<number | ''>('')

const activeTab = ref<'detail' | 'monthly'>('monthly')

// Filtres / tri du détail (appliqués côté serveur, débouncés) + regroupement par RDV (côté client)
const dF = reactive({
  fId: [] as number[],
  fCommercial: [] as string[],
  fAuteur: [] as string[],
  fRegion: [] as string[],
  fEquipe: [] as string[],
  fAgence: [] as string[],
  fStatut: [] as string[],
  fHasOrder: '' as '' | 'yes' | 'no',
  fRdvFrom: '',
  fRdvTo: '',
})
const sortBy = ref<H3aSortBy | null>(null)
const sortDir = ref<'asc' | 'desc'>('asc')
const groupedByRdv = ref(false)
const showFilterModal = ref(false)

const filters = computed(() => ({
  year: selectedYear.value,
  bu: selectedBu.value,
  dateField: selectedDateField.value,
  matchKey: selectedMatchKey.value,
  windowMonths: selectedWindowMonths.value,
  commercialId: selectedCommercial.value === '' ? undefined : Number(selectedCommercial.value),
  fId: dF.fId,
  fCommercial: dF.fCommercial,
  fAuteur: dF.fAuteur,
  fRegion: dF.fRegion,
  fEquipe: dF.fEquipe,
  fAgence: dF.fAgence,
  fStatut: dF.fStatut,
  fHasOrder: dF.fHasOrder || undefined,
  fRdvFrom: dF.fRdvFrom || undefined,
  fRdvTo: dF.fRdvTo || undefined,
  sortBy: sortBy.value ?? undefined,
  sortDir: sortDir.value,
}))

// Options des sélecteurs de filtre (issues des facettes du dataset).
const idOptions = computed(() => store.facets.ids.map((v) => ({ id: v, text: String(v) })))
const commercialOptions = computed(() => store.facets.commercials.map((v) => ({ id: v, text: v })))
const auteurOptions = computed(() => store.facets.auteurs.map((v) => ({ id: v, text: v })))
const regionOptions = computed(() => store.facets.regions.map((v) => ({ id: v, text: v })))
const equipeOptions = computed(() => store.facets.equipes.map((v) => ({ id: v, text: v })))
const agenceOptions = computed(() => store.facets.agences.map((v) => ({ id: v, text: v })))
const statutOptions = computed(() => store.facets.statuts.map((v) => ({ id: v, text: v })))

// Récapitulatif des filtres actifs (chips au-dessus du tableau).
const HAS_ORDER_LABELS: Record<'yes' | 'no', string> = { yes: 'Avec commande', no: 'Sans commande' }
const activeFilters = computed(() => {
  const out: { key: string; label: string; text: string }[] = []
  if (dF.fId.length) out.push({ key: 'fId', label: 'ID', text: dF.fId.join(', ') })
  if (dF.fCommercial.length) out.push({ key: 'fCommercial', label: 'Commercial', text: dF.fCommercial.join(', ') })
  if (dF.fAuteur.length) out.push({ key: 'fAuteur', label: 'Auteur', text: dF.fAuteur.join(', ') })
  if (dF.fRegion.length) out.push({ key: 'fRegion', label: 'Région', text: dF.fRegion.join(', ') })
  if (dF.fEquipe.length) out.push({ key: 'fEquipe', label: 'Équipe', text: dF.fEquipe.join(', ') })
  if (dF.fAgence.length) out.push({ key: 'fAgence', label: 'Agence', text: dF.fAgence.join(', ') })
  if (dF.fStatut.length) out.push({ key: 'fStatut', label: 'Statut', text: dF.fStatut.join(', ') })
  if (dF.fHasOrder) out.push({ key: 'fHasOrder', label: 'Commande', text: HAS_ORDER_LABELS[dF.fHasOrder] })
  if (dF.fRdvFrom || dF.fRdvTo) {
    out.push({ key: 'fRdv', label: 'Date RDV', text: `${dF.fRdvFrom || '…'} → ${dF.fRdvTo || '…'}` })
  }
  return out
})
const hasActiveFilters = computed(() => activeFilters.value.length > 0)

function clearFilter(key: string) {
  switch (key) {
    case 'fId': dF.fId = []; break
    case 'fCommercial': dF.fCommercial = []; break
    case 'fAuteur': dF.fAuteur = []; break
    case 'fRegion': dF.fRegion = []; break
    case 'fEquipe': dF.fEquipe = []; break
    case 'fAgence': dF.fAgence = []; break
    case 'fStatut': dF.fStatut = []; break
    case 'fHasOrder': dF.fHasOrder = ''; break
    case 'fRdv': dF.fRdvFrom = ''; dF.fRdvTo = ''; break
  }
}

const showSplit = computed(() => store.productSplit)

async function refresh(force = false) {
  await store.fetchData({ ...filters.value, refresh: force })
}

// Changement de BU / année / date de référence : on réinitialise le commercial,
// puis on recharge la liste des commerciaux + les données.
async function onScopeChange() {
  selectedCommercial.value = ''
  await Promise.all([
    store.fetchCommercials(selectedYear.value, selectedDateField.value, selectedBu.value),
    refresh(),
  ])
}

onMounted(async () => {
  await Promise.allSettled([
    store.fetchCommercials(selectedYear.value, selectedDateField.value, selectedBu.value),
    refresh(),
  ])
})

async function onExport(format: H3aExportFormat) {
  await store.exportFile(filters.value, activeTab.value, format)
}

// ── Éditeur des saisies mensuelles (objectif / coût) ──────────────────────────
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]
const showSettings = ref(false)
const settingsRows = ref<H3aMonthlySetting[]>([])

async function openSettings() {
  settingsRows.value = await store.fetchSettings(selectedBu.value, selectedYear.value)
  showSettings.value = true
}

function cleanNum(v: number | null | undefined | string): number | null {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

async function onSaveSettings() {
  // Un champ vidé revient à null (pas une chaîne vide).
  const items = settingsRows.value.map((r) => ({
    month: r.month,
    objectif: cleanNum(r.objectif),
    coutPrestation: cleanNum(r.coutPrestation),
  }))
  const ok = await store.saveSettings(selectedBu.value, selectedYear.value, items)
  if (ok) {
    showSettings.value = false
    await refresh() // recalcule les colonnes dérivées (appliquées côté serveur, post-cache)
  }
}

// ── Helpers d'affichage ───────────────────────────────────────────────────────
function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
}
function formatPct(value: number | null | undefined) {
  if (value === null || value === undefined) return '—'
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
}
function formatNum(value: number | null | undefined) {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('fr-FR')
}

const generatedAtLabel = computed(() =>
  store.generatedAt
    ? `Calculé le ${new Date(store.generatedAt).toLocaleString('fr-FR')}`
    : '',
)

const detailTabLabel = computed(() => (store.orderBased ? 'Détail commandes' : 'Détail RDV'))
const emptyLabel = computed(() =>
  store.orderBased ? 'Aucune commande pour ces filtres' : 'Aucun RDV pour ces filtres',
)

// En-têtes du tableau mensuel, calqués sur le modèle métier (adaptés ODY-C VAD).
// Les mentions longues sont en infobulle (title) pour garder le tableau lisible.
const cols = computed(() => {
  const vad = store.orderBased
  return {
    objectif: 'Objectif mensuel',
    niveau: "Niveau d'atteinte de l'objectif",
    generes: vad ? 'Nb opportunité' : 'RDV "générés"',
    valides: vad ? 'Nb commande validé' : 'RDV statut "validés"',
    tauxQualif: 'Taux qualification',
    annules: vad ? 'Nb opportunité perdu' : 'RDV statut "annulé"',
    annulesSub: vad ? '' : "tout type d'annulation (y compris doublon client Unixo et Ody-c)",
    tauxAnnul: "Taux d'annulation (%)",
    ca: 'CA généré',
    marge: 'Marge générée',
    margeSub: `sur un SIRET similaire dans les ${selectedWindowMonths.value} mois suivant le RDV`,
    margeMoyenne: vad ? 'Marge moyenne par "commande validée"' : 'Marge moyenne par "RDV validé"',
    coutPresta: 'Coût prestation (selon facture)',
    coutGenere: vad ? 'Coût par opportunité' : 'Coût par "RDV généré"',
    coutValide: vad ? 'Coût par "commande validée"' : 'Cout par "RDV validé"',
    roi: 'ROI',
    roiSub: "(gain − coût) / coût × 100",
  }
})

// Totaux mensuels (pied de tableau)
const monthlyTotals = computed(() => {
  const t = { rdvValides: 0, rdvAnnules: 0, margeGeneree: 0, objectif: 0, coutPrestation: 0, nbCommandes: 0 }
  let generes: number | null = 0 // null dès qu'un mois a « générés » inconnu (ex. VAD)
  for (const m of store.monthly) {
    if (m.rdvGeneres == null) generes = null
    else if (generes != null) generes += m.rdvGeneres
    t.rdvValides += m.rdvValides
    t.rdvAnnules += m.rdvAnnules
    t.margeGeneree += m.margeGeneree
    t.objectif += m.objectif ?? 0
    t.coutPrestation += m.coutPrestation ?? 0
    t.nbCommandes += m.nbCommandes
  }
  return {
    ...t,
    rdvGeneres: generes,
    caGenere: null as number | null, // CA non connu à ce jour
    tauxQualification: generes ? (t.rdvValides / generes) * 100 : null,
    tauxAnnulation: generes ? (t.rdvAnnules / generes) * 100 : null,
    margeMoyenneParRdvValide: t.rdvValides ? t.margeGeneree / t.rdvValides : 0,
    margeMoyenneParCommande: t.nbCommandes ? t.margeGeneree / t.nbCommandes : 0,
    niveauAtteinte: t.objectif && generes != null ? (generes / t.objectif) * 100 : null,
    coutParRdvGenere: generes && t.coutPrestation ? t.coutPrestation / generes : null,
    coutParRdvValide: t.rdvValides && t.coutPrestation ? t.coutPrestation / t.rdvValides : null,
    roi: t.coutPrestation ? ((t.margeGeneree - t.coutPrestation) / t.coutPrestation) * 100 : null,
  }
})

// ── Détail : tri (serveur), filtres (serveur, débouncés), regroupement par RDV (client) ──
function toggleSort(field: H3aSortBy) {
  if (sortBy.value === field) sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  else {
    sortBy.value = field
    sortDir.value = 'asc'
  }
  refresh()
}
function sortInd(field: H3aSortBy) {
  if (sortBy.value !== field) return ''
  return sortDir.value === 'asc' ? ' ▲' : ' ▼'
}
function resetDetailFilters() {
  Object.assign(dF, {
    fId: [], fCommercial: [], fAuteur: [], fRegion: [],
    fEquipe: [], fAgence: [], fStatut: [], fHasOrder: '', fRdvFrom: '', fRdvTo: '',
  })
}

let detailFilterTimer: ReturnType<typeof setTimeout> | undefined
watch(dF, () => {
  clearTimeout(detailFilterTimer)
  detailFilterTimer = setTimeout(() => refresh(), 350)
})

// Regroupement par RDV (somme des nb_cmd / marge multi-source en une ligne).
const displayedDetail = computed<Array<H3aDetailRow & { key: string }>>(() => {
  if (!groupedByRdv.value) return store.detail.map((r, i) => ({ ...r, key: `${r.rdvId}-${i}` }))
  const map = new Map<number, H3aDetailRow & { key: string }>()
  const srcs = new Map<number, Set<string>>()
  for (const r of store.detail) {
    let g = map.get(r.rdvId)
    if (!g) {
      g = { ...r, key: String(r.rdvId) }
      map.set(r.rdvId, g)
      srcs.set(r.rdvId, new Set())
    } else {
      g.nbCommandes += r.nbCommandes
      g.montantTotal += r.montantTotal
      g.margeTotale += r.margeTotale
      g.margeFixe += r.margeFixe
      g.margeMobile += r.margeMobile
    }
    if (r.source) srcs.get(r.rdvId)!.add(r.source)
  }
  for (const [id, g] of map) {
    const s = srcs.get(id)
    g.source = s && s.size ? Array.from(s).join(', ') : null
  }
  return Array.from(map.values())
})
</script>

<template>
  <div ref="pageRoot" :style="{ height: pageHeight }" class="p-6 flex flex-col gap-6 overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3 shrink-0">
      <Bs2Title
        title="Statistiques RDV H3A"
        :icon="ChartBarIcon"
        subtitle="Performance par business unit : qualification, annulation et marge générée"
      />
      <span v-if="generatedAtLabel" class="text-xs text-base-content/40">{{ generatedAtLabel }}</span>
    </div>

    <!-- Notes / avertissements métier -->
    <div v-for="(note, i) in store.notes" :key="i" role="alert" class="alert alert-warning py-2 text-sm shrink-0">
      <span>{{ note }}</span>
    </div>

    <!-- Filtres + actions -->
    <div class="flex items-end gap-3 flex-wrap shrink-0">
      <div>
        <label class="label py-0"><span class="label-text text-xs">BU</span></label>
        <select v-model="selectedBu" class="select select-sm select-bordered" @change="onScopeChange">
          <option v-for="b in BU_OPTIONS" :key="b.value" :value="b.value">{{ b.label }}</option>
        </select>
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Année</span></label>
        <select v-model.number="selectedYear" class="select select-sm select-bordered" @change="onScopeChange">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Date de référence</span></label>
        <select v-model="selectedDateField" class="select select-sm select-bordered" @change="onScopeChange">
          <option value="createAt">Date d'émission</option>
          <option value="date">Date du RDV</option>
        </select>
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Rapprochement</span></label>
        <select v-model="selectedMatchKey" class="select select-sm select-bordered" @change="refresh()">
          <option value="siret">Par SIRET</option>
          <option value="siren">Par SIREN</option>
        </select>
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Fenêtre (mois)</span></label>
        <select v-model.number="selectedWindowMonths" class="select select-sm select-bordered" @change="refresh()">
          <option v-for="w in WINDOW_OPTIONS" :key="w" :value="w">{{ w }} mois</option>
        </select>
      </div>
      <div>
        <label class="label py-0"><span class="label-text text-xs">Commercial</span></label>
        <select v-model="selectedCommercial" class="select select-sm select-bordered w-56" @change="refresh()">
          <option value="">Tous les commerciaux</option>
          <option v-for="c in store.commercials" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <button class="btn btn-sm btn-ghost" :disabled="store.isLoading" @click="refresh(true)">
        <ArrowPathIcon class="size-4" :class="{ 'animate-spin': store.isLoading }" /> Recalculer
      </button>

      <div class="ml-auto flex items-end gap-2">
        <button class="btn btn-sm btn-ghost" @click="openSettings">
          <Cog6ToothIcon class="size-4" /> Objectifs / coûts
        </button>
        <button class="btn btn-sm btn-primary" :disabled="store.isExporting" @click="onExport('xlsx')">
          <ArrowDownTrayIcon class="size-4" /> Export XLSX
        </button>
        <button class="btn btn-sm btn-outline" :disabled="store.isExporting" @click="onExport('csv')">
          <ArrowDownTrayIcon class="size-4" /> CSV
        </button>
      </div>
    </div>

    <!-- Bascule détail / mensuel (toggle segmenté) -->
    <div class="join shrink-0">
      <button
        type="button"
        class="btn btn-sm join-item"
        :class="activeTab === 'monthly' ? 'btn-primary' : 'btn-neutral btn-outline'"
        @click="activeTab = 'monthly'"
      >
        Stats mensuelles
      </button>
      <button
        type="button"
        class="btn btn-sm join-item"
        :class="activeTab === 'detail' ? 'btn-primary' : 'btn-neutral btn-outline'"
        @click="activeTab = 'detail'"
      >
        {{ detailTabLabel }}
        <span class="badge badge-sm badge-ghost ml-1">{{ store.detail.length }}</span>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="store.isLoading" class="flex flex-1 items-center justify-center py-12">
      <span class="loading loading-spinner loading-lg" />
    </div>

    <!-- Détail -->
    <div v-else-if="activeTab === 'detail'" class="flex flex-col flex-1 min-h-0">
      <!-- Barre : Filtrer / Réinitialiser / filtres actifs (chips) / regroupement -->
      <div class="flex items-center gap-2 flex-wrap mb-3 shrink-0">
        <button class="btn btn-sm btn-outline" @click="showFilterModal = true">
          <FunnelIcon class="size-4" /> Filtrer
          <span v-if="hasActiveFilters" class="badge badge-primary badge-sm">{{ activeFilters.length }}</span>
        </button>
        <button v-if="hasActiveFilters" class="btn btn-sm btn-ghost" @click="resetDetailFilters">Réinitialiser</button>
        <div
          v-for="f in activeFilters"
          :key="f.key"
          class="badge badge-ghost gap-1 max-w-xs"
          :title="`${f.label} : ${f.text}`"
        >
          <span class="font-medium">{{ f.label }} :</span>
          <span class="truncate">{{ f.text }}</span>
          <button class="ml-0.5 hover:text-error" title="Retirer ce filtre" @click="clearFilter(f.key)">✕</button>
        </div>
        <label class="label cursor-pointer gap-2 ml-auto">
          <input v-model="groupedByRdv" type="checkbox" class="toggle toggle-sm" />
          <span class="label-text text-xs">Regrouper par RDV</span>
        </label>
      </div>

      <div v-if="displayedDetail.length" class="flex-1 min-h-0 overflow-auto rounded-xl border border-base-300">
        <table class="table table-xs table-pin-rows w-full">
          <thead>
            <tr>
              <th class="cursor-pointer select-none" @click="toggleSort('id')">ID{{ sortInd('id') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('commercial')">Commercial{{ sortInd('commercial') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('auteur')">Auteur{{ sortInd('auteur') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('bu')">BU{{ sortInd('bu') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('region')">Région{{ sortInd('region') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('equipe')">Équipe{{ sortInd('equipe') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('agence')">Agence{{ sortInd('agence') }}</th>
              <th>Émission</th>
              <th class="cursor-pointer select-none" @click="toggleSort('dateRdv')">RDV{{ sortInd('dateRdv') }}</th>
              <th class="cursor-pointer select-none" @click="toggleSort('statut')">Statut{{ sortInd('statut') }}</th>
              <th>Société</th>
              <th>SIRET</th>
              <th>Source</th>
              <th class="text-right">Nb cmd</th>
              <th class="text-right">Montant</th>
              <th class="text-right">Marge tot.</th>
              <th v-if="showSplit" class="text-right">Marge fixe</th>
              <th v-if="showSplit" class="text-right">Marge mobile</th>
              <th>Commentaire</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in displayedDetail" :key="row.key" class="hover">
              <td class="font-mono text-xs">{{ row.rdvId }}</td>
              <td>{{ row.commercial || '—' }}</td>
              <td>{{ row.auteur || '—' }}</td>
              <td>{{ row.bu || '—' }}</td>
              <td>{{ row.region || '—' }}</td>
              <td>{{ row.equipe || '—' }}</td>
              <td>{{ row.agence || '—' }}</td>
              <td class="text-xs">{{ formatDate(row.dateEmission) }}</td>
              <td class="text-xs">{{ formatDate(row.dateRdv) }}</td>
              <td class="text-xs">{{ row.statut }}</td>
              <td>{{ row.societe || '—' }}</td>
              <td class="font-mono text-xs">{{ row.siret || row.siren }}</td>
              <td class="text-xs">{{ row.source || '—' }}</td>
              <td class="text-right">{{ row.nbCommandes }}</td>
              <td class="text-right">{{ formatMoney(row.montantTotal) }}</td>
              <td class="text-right font-medium">{{ formatMoney(row.margeTotale) }}</td>
              <td v-if="showSplit" class="text-right text-xs">{{ formatMoney(row.margeFixe) }}</td>
              <td v-if="showSplit" class="text-right text-xs">{{ formatMoney(row.margeMobile) }}</td>
              <td class="max-w-xs truncate text-xs" :title="row.commentaire ?? ''">{{ row.commentaire || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-base-content/40">
        <ChartBarIcon class="size-12" />
        <p>{{ emptyLabel }}</p>
      </div>

      <!-- Modale de filtres du détail -->
      <dialog class="modal" :class="{ 'modal-open': showFilterModal }">
        <div class="modal-box max-w-2xl overflow-visible">
          <h3 class="font-semibold text-lg mb-4">Filtrer le détail</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="label py-0"><span class="label-text text-xs">ID</span></label>
              <AvatarMultiselect v-model="dF.fId" :options="idOptions" unit="ID" unit-plural="IDs" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Commercial</span></label>
              <AvatarMultiselect v-model="dF.fCommercial" :options="commercialOptions" unit="commercial" unit-plural="commerciaux" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Auteur</span></label>
              <AvatarMultiselect v-model="dF.fAuteur" :options="auteurOptions" unit="auteur" unit-plural="auteurs" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Région</span></label>
              <AvatarMultiselect v-model="dF.fRegion" :options="regionOptions" unit="région" unit-plural="régions" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Équipe</span></label>
              <AvatarMultiselect v-model="dF.fEquipe" :options="equipeOptions" unit="équipe" unit-plural="équipes" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Agence</span></label>
              <AvatarMultiselect v-model="dF.fAgence" :options="agenceOptions" unit="agence" unit-plural="agences" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Statut</span></label>
              <AvatarMultiselect v-model="dF.fStatut" :options="statutOptions" unit="statut" unit-plural="statuts" hide-avatar />
            </div>
            <div>
              <label class="label py-0"><span class="label-text text-xs">Commande</span></label>
              <select v-model="dF.fHasOrder" class="select select-sm select-bordered w-full">
                <option value="">Toutes</option>
                <option value="yes">Avec commande</option>
                <option value="no">Sans commande</option>
              </select>
            </div>
            <div class="sm:col-span-2">
              <label class="label py-0"><span class="label-text text-xs">Date du RDV (du / au)</span></label>
              <div class="flex items-center gap-2">
                <input v-model="dF.fRdvFrom" type="date" class="input input-sm input-bordered w-full" />
                <span class="text-base-content/40">→</span>
                <input v-model="dF.fRdvTo" type="date" class="input input-sm input-bordered w-full" />
              </div>
            </div>
          </div>
          <div class="modal-action">
            <button v-if="hasActiveFilters" class="btn btn-ghost" @click="resetDetailFilters">Tout réinitialiser</button>
            <button class="btn btn-primary" @click="showFilterModal = false">Voir les résultats</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop" @click="showFilterModal = false"><button>close</button></form>
      </dialog>
    </div>

    <!-- Stats mensuelles -->
    <div v-else class="flex flex-col flex-1 min-h-0">
      <p class="text-xs text-base-content/50 mb-2 shrink-0">
        Objectif et coût de prestation se saisissent via « Objectifs / coûts ». Niveau d'atteinte, coûts par RDV et ROI en sont dérivés.
      </p>
      <div class="flex-1 min-h-0 overflow-auto rounded-xl border border-base-300">
        <table class="table table-sm table-pin-rows w-full">
          <thead>
            <tr class="align-bottom">
              <th>Mois</th>
              <th class="text-right whitespace-normal">{{ cols.objectif }}</th>
              <th class="text-right whitespace-normal">{{ cols.niveau }}</th>
              <th class="text-right whitespace-normal">{{ cols.generes }}</th>
              <th class="text-right whitespace-normal">{{ cols.valides }}</th>
              <th class="text-right whitespace-normal">{{ cols.tauxQualif }}</th>
              <th class="text-right whitespace-normal">
                <div>{{ cols.annules }}</div>
                <div v-if="cols.annulesSub" class="text-[10px] font-normal italic opacity-60">{{ cols.annulesSub }}</div>
              </th>
              <th class="text-right whitespace-normal">{{ cols.tauxAnnul }}</th>
              <th class="text-right whitespace-normal">{{ cols.ca }}</th>
              <th class="text-right whitespace-normal">
                <div>{{ cols.marge }}</div>
                <div class="text-[10px] font-normal italic opacity-60">{{ cols.margeSub }}</div>
              </th>
              <th class="text-right whitespace-normal">Nb commandes</th>
              <th class="text-right whitespace-normal">Marge moyenne / commande</th>
              <th class="text-right whitespace-normal">{{ cols.margeMoyenne }}</th>
              <th class="text-right whitespace-normal">{{ cols.coutPresta }}</th>
              <th class="text-right whitespace-normal">{{ cols.coutGenere }}</th>
              <th class="text-right whitespace-normal">{{ cols.coutValide }}</th>
              <th class="text-right whitespace-normal">
                <div>{{ cols.roi }}</div>
                <div class="text-[10px] font-normal italic opacity-60">{{ cols.roiSub }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in store.monthly" :key="m.month" class="hover">
              <td class="font-medium">{{ m.monthLabel }}</td>
              <td class="text-right">{{ formatNum(m.objectif) }}</td>
              <td class="text-right">{{ formatPct(m.niveauAtteinte) }}</td>
              <td class="text-right">{{ formatNum(m.rdvGeneres) }}</td>
              <td class="text-right">{{ m.rdvValides }}</td>
              <td class="text-right">{{ formatPct(m.tauxQualification) }}</td>
              <td class="text-right">{{ m.rdvAnnules }}</td>
              <td class="text-right">{{ formatPct(m.tauxAnnulation) }}</td>
              <td class="text-right">{{ formatMoney(m.caGenere) }}</td>
              <td class="text-right font-medium">{{ formatMoney(m.margeGeneree) }}</td>
              <td class="text-right">{{ m.nbCommandes }}</td>
              <td class="text-right">{{ formatMoney(m.margeMoyenneParCommande) }}</td>
              <td class="text-right">{{ formatMoney(m.margeMoyenneParRdvValide) }}</td>
              <td class="text-right">{{ formatMoney(m.coutPrestation) }}</td>
              <td class="text-right">{{ formatMoney(m.coutParRdvGenere) }}</td>
              <td class="text-right">{{ formatMoney(m.coutParRdvValide) }}</td>
              <td class="text-right">{{ formatPct(m.roi) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-semibold bg-base-200">
              <td>Total {{ selectedYear }}</td>
              <td class="text-right">{{ monthlyTotals.objectif || '—' }}</td>
              <td class="text-right">{{ formatPct(monthlyTotals.niveauAtteinte) }}</td>
              <td class="text-right">{{ formatNum(monthlyTotals.rdvGeneres) }}</td>
              <td class="text-right">{{ monthlyTotals.rdvValides }}</td>
              <td class="text-right">{{ formatPct(monthlyTotals.tauxQualification) }}</td>
              <td class="text-right">{{ monthlyTotals.rdvAnnules }}</td>
              <td class="text-right">{{ formatPct(monthlyTotals.tauxAnnulation) }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.caGenere) }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.margeGeneree) }}</td>
              <td class="text-right">{{ monthlyTotals.nbCommandes }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.margeMoyenneParCommande) }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.margeMoyenneParRdvValide) }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.coutPrestation) }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.coutParRdvGenere) }}</td>
              <td class="text-right">{{ formatMoney(monthlyTotals.coutParRdvValide) }}</td>
              <td class="text-right">{{ formatPct(monthlyTotals.roi) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Modale : saisie objectifs / coûts (par BU + année) -->
    <dialog class="modal" :class="{ 'modal-open': showSettings }">
      <div class="modal-box max-w-2xl">
        <h3 class="font-semibold text-lg mb-1">Objectifs &amp; coûts de prestation</h3>
        <p class="text-sm text-base-content/60 mb-4">
          BU <span class="badge badge-sm">{{ selectedBu }}</span> — année {{ selectedYear }}
        </p>
        <div class="overflow-y-auto max-h-[60vh]">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Mois</th>
                <th>Objectif mensuel</th>
                <th>Coût prestation (€)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in settingsRows" :key="row.month">
                <td class="font-medium">{{ MONTHS_FR[row.month - 1] }}</td>
                <td>
                  <input
                    v-model.number="row.objectif"
                    type="number"
                    min="0"
                    step="1"
                    class="input input-sm input-bordered w-32"
                    placeholder="—"
                  />
                </td>
                <td>
                  <input
                    v-model.number="row.coutPrestation"
                    type="number"
                    min="0"
                    step="0.01"
                    class="input input-sm input-bordered w-32"
                    placeholder="—"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-action">
          <button class="btn btn-ghost" @click="showSettings = false">Annuler</button>
          <button class="btn btn-primary" :disabled="store.isSavingSettings" @click="onSaveSettings">
            <span v-if="store.isSavingSettings" class="loading loading-spinner loading-xs" />
            Enregistrer
          </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop" @click="showSettings = false"><button>close</button></form>
    </dialog>
  </div>
</template>
